/**
 * SettingsPage — user-scoped preferences + account info + about.
 *
 * Direction: traditional finance app (Bloomberg / FactSet). Single-column
 * scrollable list of section cards. Each card is a Paper with a left-side
 * label/description and a right-side control. Slate-blue accent only;
 * semantic green/red reserved for status.
 */

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Divider,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  alpha,
} from '@mui/material';
import { Sun, Moon, Monitor, Rows3, Rows2, AlignJustify, User as UserIcon, Info } from 'lucide-react';
import {
  useAppearance,
  useDensity,
  type AppearanceSetting,
  type Density,
} from '../theme/AppearanceContext';
import { useAuth } from '../auth/AuthProvider';

// ---------------------------------------------------------------------------
// Section primitive — visual card with label/description left, control right.
// ---------------------------------------------------------------------------

interface SettingSectionProps {
  kicker: string;
  title: string;
  description: string;
  icon?: React.ReactNode;
  control: React.ReactNode;
  currentValue?: string;
}

const SettingSection: React.FC<SettingSectionProps> = ({
  kicker,
  title,
  description,
  icon,
  control,
  currentValue,
}) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
        gap: 3,
        alignItems: 'center',
      }}
    >
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          {icon && (
            <Box sx={{ color: theme.palette.text.secondary, display: 'flex' }}>
              {icon}
            </Box>
          )}
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontWeight: 600,
              fontSize: '0.6875rem',
            }}
          >
            {kicker}
          </Typography>
        </Stack>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.05rem', mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          {description}
        </Typography>
        {currentValue && (
          <Chip
            label={`Current: ${currentValue}`}
            size="small"
            sx={{
              mt: 1.5,
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
              fontWeight: 600,
              fontSize: '0.6875rem',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.20)}`,
              height: 22,
            }}
          />
        )}
      </Box>
      <Box sx={{ justifySelf: { xs: 'flex-start', md: 'flex-end' } }}>{control}</Box>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Read-only info row — used in Account + About cards.
// ---------------------------------------------------------------------------

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        py: 1.25,
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.text.secondary,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 600,
          fontSize: '0.6875rem',
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontFamily: 'monospace', color: theme.palette.text.primary, fontWeight: 500 }}
      >
        {value}
      </Typography>
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Read-only info card — wraps a stack of InfoRows.
// ---------------------------------------------------------------------------

const InfoCard: React.FC<{
  kicker: string;
  title: string;
  description: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}> = ({ kicker, title, description, icon, children }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        {icon && (
          <Box sx={{ color: theme.palette.text.secondary, display: 'flex' }}>{icon}</Box>
        )}
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontWeight: 600,
            fontSize: '0.6875rem',
          }}
        >
          {kicker}
        </Typography>
      </Stack>
      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.05rem', mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
        {description}
      </Typography>
      <Box>{children}</Box>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Toggle controls (re-implemented here so the Settings page is self-contained
// and the AppBar can drop its toggles cleanly).
// ---------------------------------------------------------------------------

const ICON_PX = 16;

const AppearanceControl: React.FC = () => {
  const theme = useTheme();
  const { mode, setMode } = useAppearance();

  return (
    <ToggleButtonGroup
      exclusive
      value={mode}
      onChange={(_, v: AppearanceSetting | null) => v && setMode(v)}
      size="small"
      aria-label="Appearance mode"
      sx={{
        '& .MuiToggleButton-root': {
          px: 1.5,
          py: 0.5,
          gap: 0.75,
          color: theme.palette.text.secondary,
          border: `1px solid ${theme.palette.divider}`,
          textTransform: 'none',
          fontSize: '0.8125rem',
          fontWeight: 500,
          '&.Mui-selected': {
            color: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            borderColor: theme.palette.primary.main,
          },
        },
      }}
    >
      <ToggleButton value="light" aria-label="Light">
        <Sun size={ICON_PX} /> Light
      </ToggleButton>
      <ToggleButton value="dark" aria-label="Dark">
        <Moon size={ICON_PX} /> Dark
      </ToggleButton>
      <ToggleButton value="auto" aria-label="Auto">
        <Monitor size={ICON_PX} /> Auto
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

const DensityControl: React.FC = () => {
  const theme = useTheme();
  const { density, setDensity } = useDensity();

  return (
    <ToggleButtonGroup
      exclusive
      value={density}
      onChange={(_, v: Density | null) => v && setDensity(v)}
      size="small"
      aria-label="Row density"
      sx={{
        '& .MuiToggleButton-root': {
          px: 1.5,
          py: 0.5,
          gap: 0.75,
          color: theme.palette.text.secondary,
          border: `1px solid ${theme.palette.divider}`,
          textTransform: 'none',
          fontSize: '0.8125rem',
          fontWeight: 500,
          '&.Mui-selected': {
            color: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            borderColor: theme.palette.primary.main,
          },
        },
      }}
    >
      <ToggleButton value="compact" aria-label="Compact">
        <Rows3 size={ICON_PX} /> Compact
      </ToggleButton>
      <ToggleButton value="comfortable" aria-label="Comfortable">
        <Rows2 size={ICON_PX} /> Comfortable
      </ToggleButton>
      <ToggleButton value="wide" aria-label="Wide">
        <AlignJustify size={ICON_PX} /> Wide
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

// ---------------------------------------------------------------------------
// Resolved-value labels (shown as "Current:" chip).
// ---------------------------------------------------------------------------

const APPEARANCE_LABEL: Record<AppearanceSetting, string> = {
  light: 'Light',
  dark: 'Dark',
  auto: 'Auto (system)',
};

const DENSITY_LABEL: Record<Density, string> = {
  compact: 'Compact',
  comfortable: 'Comfortable',
  wide: 'Wide',
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const SettingsPage: React.FC = () => {
  const theme = useTheme();
  const { mode, resolvedMode } = useAppearance();
  const { density } = useDensity();
  const { user } = useAuth();

  // Build a human-readable "Current:" string for Appearance.
  const appearanceCurrent =
    mode === 'auto'
      ? `Auto — currently ${resolvedMode}`
      : APPEARANCE_LABEL[mode];

  return (
    <Box sx={{ p: 3, maxWidth: 920, mx: 'auto' }}>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontWeight: 600,
            fontSize: '0.6875rem',
          }}
        >
          USER · SETTINGS
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 600, mt: 0.5, mb: 0.5 }}>
          Settings
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
          Manage your display preferences and review account details. Changes apply
          immediately and persist on this device.
        </Typography>
      </Box>

      <Stack spacing={2.5}>
        <SettingSection
          kicker="DISPLAY"
          title="Appearance"
          description="Switch between a clean white surface for daytime use and a dark surface for overnight monitoring. Auto follows your operating-system preference."
          icon={mode === 'dark' ? <Moon size={14} /> : mode === 'light' ? <Sun size={14} /> : <Monitor size={14} />}
          control={<AppearanceControl />}
          currentValue={appearanceCurrent}
        />

        <SettingSection
          kicker="DISPLAY"
          title="Table Density"
          description="Compact fits more rows on screen for trading screens; Comfortable balances readability; Wide gives generous spacing for long sessions."
          icon={<Rows3 size={14} />}
          control={<DensityControl />}
          currentValue={DENSITY_LABEL[density]}
        />

        <InfoCard
          kicker="ACCOUNT"
          title="Account"
          description="Authenticated session details. Sign out from the user menu in the top-right."
          icon={<UserIcon size={14} />}
        >
          <InfoRow label="Username" value={user?.username ?? '—'} />
          <InfoRow label="Role" value={user?.role ?? '—'} />
          <InfoRow
            label="Tenant"
            value={
              <Box component="span" sx={{ fontSize: '0.75rem' }}>
                {localStorage.getItem('tenant_id') ?? '—'}
              </Box>
            }
          />
        </InfoCard>

        <InfoCard
          kicker="PLATFORM"
          title="About"
          description="QFinPricer — QuantLib-powered cross-asset derivatives pricing."
          icon={<Info size={14} />}
        >
          <InfoRow label="Platform" value="qfinpricer" />
          <InfoRow label="Frontend" value={import.meta.env.MODE ?? 'production'} />
          <InfoRow
            label="Host"
            value={
              <Box component="span" sx={{ fontSize: '0.75rem' }}>
                {window.location.host}
              </Box>
            }
          />
        </InfoCard>
      </Stack>

      <Divider sx={{ mt: 5, mb: 2 }} />
      <Typography
        variant="caption"
        sx={{ color: theme.palette.text.disabled, display: 'block', textAlign: 'center' }}
      >
        Preferences are saved in localStorage on this device — they do not sync across browsers.
      </Typography>
    </Box>
  );
};

export default SettingsPage;
